import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { User, Shield, Bell, Settings as SettingsIcon, Key, ExternalLink, Save, Loader2, CheckCircle, Users, Plug, Package, Crown, Lock, Unlock, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import NotificationPreferences from '@/components/NotificationPreferences';
import UserManagement from '@/components/UserManagement';
import IntegrationsSettings from '@/components/IntegrationsSettings';
import RBACSettings from '@/components/RBACSettings';
import { useTenant } from '@/contexts/TenantContext';
import { getJiraConfig, saveJiraConfig, testJiraConnection, getAdminDBConfig, saveAdminDBConfig, testAdminDBConfig } from '@/services/dpdpa';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

const JiraConfigSection = () => {
  const [config, setConfig] = useState({ base_url: '', api_token: '', user_email: '', project_key: '' });
  const [loadingJira, setLoadingJira] = useState(true);
  const [savingJira, setSavingJira] = useState(false);
  const [testingJira, setTestingJira] = useState(false);
  const [jiraConnected, setJiraConnected] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await getJiraConfig();
        setConfig({
          base_url: data.base_url || '',
          api_token: data.api_token === '***' ? '' : (data.api_token || ''),
          user_email: data.user_email || '',
          project_key: data.project_key || '',
        });
        setJiraConnected(!!data.configured);
      } catch (err) {
        console.error('Failed to load Jira config:', err);
      } finally {
        setLoadingJira(false);
      }
    };
    loadConfig();
  }, []);

  const handleSaveJira = async () => {
    if (!config.base_url || !config.api_token || !config.user_email || !config.project_key) {
      toast.error('All Jira fields are required');
      return;
    }
    try {
      setSavingJira(true);
      await saveJiraConfig(config);
      toast.success('Jira configuration saved');
      setJiraConnected(true);
    } catch (err) {
      toast.error('Failed to save Jira config');
    } finally {
      setSavingJira(false);
    }
  };

  const handleTestJira = async () => {
    try {
      setTestingJira(true);
      const result = await testJiraConnection();
      if (result.success) {
        toast.success(`Connected to Jira as ${result.user} (${result.email})`);
      } else {
        toast.error(`Jira connection failed: ${result.error}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Jira test failed');
    } finally {
      setTestingJira(false);
    }
  };

  if (loadingJira) {
    return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <Card data-testid="jira-config-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ExternalLink className="w-5 h-5" />
          Jira Integration
        </CardTitle>
        <CardDescription>Configure Jira API credentials for automatic ticket creation on DPDPA data subject requests</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {jiraConnected && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700">Jira is configured and active</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Jira Base URL</Label>
            <Input value={config.base_url} onChange={e => setConfig(prev => ({ ...prev, base_url: e.target.value }))} placeholder="https://your-domain.atlassian.net" data-testid="jira-base-url-input" />
          </div>
          <div>
            <Label>Project Key</Label>
            <Input value={config.project_key} onChange={e => setConfig(prev => ({ ...prev, project_key: e.target.value }))} placeholder="e.g. DPDPA" data-testid="jira-project-key-input" />
          </div>
          <div>
            <Label>Jira User Email</Label>
            <Input value={config.user_email} onChange={e => setConfig(prev => ({ ...prev, user_email: e.target.value }))} placeholder="admin@company.com" data-testid="jira-email-input" />
          </div>
          <div>
            <Label>API Token</Label>
            <Input type="password" value={config.api_token} onChange={e => setConfig(prev => ({ ...prev, api_token: e.target.value }))} placeholder={jiraConnected ? '••••••••••' : 'Enter Jira API token'} data-testid="jira-token-input" />
            <p className="text-xs opacity-50 mt-1">
              <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="underline">Generate API token</a>
            </p>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSaveJira} disabled={savingJira} data-testid="save-jira-config-btn">
            {savingJira ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
          <Button variant="outline" onClick={handleTestJira} disabled={testingJira || !jiraConnected} data-testid="test-jira-btn">
            {testingJira ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDBConfigSection = () => {
  const [config, setConfig] = useState({ storage_type: 'internal', connection_string: '', db_name: '', db_type: 'mongodb' });
  const [loadingDB, setLoadingDB] = useState(true);
  const [savingDB, setSavingDB] = useState(false);
  const [testingDB, setTestingDB] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await getAdminDBConfig();
        setConfig({
          storage_type: data.storage_type || 'internal',
          connection_string: data.connection_string === '***' ? '' : (data.connection_string || ''),
          db_name: data.db_name || '',
          db_type: data.db_type || 'mongodb',
        });
      } catch (err) {
        console.error('Failed to load admin DB config:', err);
      } finally {
        setLoadingDB(false);
      }
    };
    loadConfig();
  }, []);

  const handleSave = async () => {
    try {
      setSavingDB(true);
      await saveAdminDBConfig(config);
      toast.success('Admin DB configuration saved');
    } catch (err) {
      toast.error('Failed to save config');
    } finally {
      setSavingDB(false);
    }
  };

  const handleTest = async () => {
    try {
      setTestingDB(true);
      const result = await testAdminDBConfig();
      if (result.success) toast.success(result.message);
      else toast.error(result.message);
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setTestingDB(false);
    }
  };

  if (loadingDB) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <Card data-testid="admin-db-config-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Consent Storage Database</CardTitle>
        <CardDescription>Configure where DPDPA consent records and administrative data are stored</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Storage Type</Label>
          <Select value={config.storage_type} onValueChange={v => setConfig(prev => ({ ...prev, storage_type: v }))}>
            <SelectTrigger data-testid="admin-storage-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Internal MongoDB (Default)</SelectItem>
              <SelectItem value="external">External Database</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {config.storage_type === 'external' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Database Type</Label>
                <Select value={config.db_type} onValueChange={v => setConfig(prev => ({ ...prev, db_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mongodb">MongoDB</SelectItem>
                    <SelectItem value="postgresql">PostgreSQL</SelectItem>
                    <SelectItem value="mysql">MySQL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Database Name</Label>
                <Input value={config.db_name} onChange={e => setConfig(prev => ({ ...prev, db_name: e.target.value }))} placeholder="e.g. dpdpa_consent_db" data-testid="admin-db-name" />
              </div>
            </div>
            <div>
              <Label>Connection String</Label>
              <Input type="password" value={config.connection_string} onChange={e => setConfig(prev => ({ ...prev, connection_string: e.target.value }))} placeholder="mongodb://user:pass@host:port/db" data-testid="admin-db-conn-string" />
            </div>
          </>
        )}
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={savingDB} data-testid="save-admin-db-btn">
            {savingDB ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
          {config.storage_type === 'external' && (
            <Button variant="outline" onClick={handleTest} disabled={testingDB} data-testid="test-admin-db-btn">
              {testingDB ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />} Test Connection
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isSuperAdmin, isTenantAdmin, hasPermission, loading: tenantLoading, userRole, currentTenant } = useTenant();
  const [saving, setSaving] = useState(false);
  const [apiKeys, setApiKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  
  // Handle tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };
  
  // Check if user can access user management
  // Show tab if: super admin, OR tenant admin/owner, OR has user:read permission, OR user.role from localStorage is admin
  const canManageUsers = isSuperAdmin || 
    (userRole && ['admin', 'owner'].includes(userRole)) || 
    hasPermission('user:read') ||
    (user.role && ['admin', 'owner', 'super_admin'].includes(user.role));
  
  // Debug logging
  console.log('SettingsPage - canManageUsers:', canManageUsers, {
    isSuperAdmin, userRole, tenantLoading, currentTenant: currentTenant?.name, localUserRole: user.role
  });

  const osintApiKeys = [
    // Threat Intelligence - Free (no API key needed, just enable/disable)
    { key: 'malwarebazaar', name: 'MalwareBazaar', url: 'https://bazaar.abuse.ch/', required: false, free: true, noKey: true },
    { key: 'urlhaus', name: 'URLhaus', url: 'https://urlhaus.abuse.ch/', required: false, free: true, noKey: true },
    { key: 'threatfox', name: 'ThreatFox', url: 'https://threatfox.abuse.ch/', required: false, free: true, noKey: true },
    { key: 'feodotracker', name: 'Feodo Tracker', url: 'https://feodotracker.abuse.ch/', required: false, free: true, noKey: true },
    { key: 'sslbl', name: 'SSL Blacklist', url: 'https://sslbl.abuse.ch/', required: false, free: true, noKey: true },
    { key: 'greynoise', name: 'GreyNoise Community', url: 'https://viz.greynoise.io/', required: false, free: true, noKey: true },
    { key: 'internetdb', name: 'Shodan InternetDB', url: 'https://internetdb.shodan.io/', required: false, free: true, noKey: true },
    { key: 'spamhaus', name: 'Spamhaus ZEN', url: 'https://www.spamhaus.org/', required: false, free: true, noKey: true },
    { key: 'phishtank', name: 'PhishTank', url: 'https://phishtank.org/', required: false, free: true, noKey: true },
    { key: 'openphish', name: 'OpenPhish', url: 'https://openphish.com/', required: false, free: true, noKey: true },
    { key: 'circl', name: 'CIRCL Hashlookup', url: 'https://hashlookup.circl.lu/', required: false, free: true, noKey: true },
    { key: 'threatminer', name: 'ThreatMiner', url: 'https://www.threatminer.org/', required: false, free: true, noKey: true },
    { key: 'stopforumspam', name: 'StopForumSpam', url: 'https://www.stopforumspam.com/', required: false, free: true, noKey: true },
    { key: 'blocklist_de', name: 'Blocklist.de', url: 'https://www.blocklist.de/', required: false, free: true, noKey: true },
    { key: 'firehol', name: 'FireHOL', url: 'https://iplists.firehol.org/', required: false, free: true, noKey: true },
    { key: 'emergingthreats', name: 'Emerging Threats', url: 'https://rules.emergingthreats.net/', required: false, free: true, noKey: true },
    { key: 'cinsarmy', name: 'CI Army', url: 'https://cinsscore.com/', required: false, free: true, noKey: true },
    
    // Services requiring API keys
    { key: 'virustotal', name: 'VirusTotal', url: 'https://www.virustotal.com/gui/join-us', required: true, free: false },
    { key: 'abuseipdb', name: 'AbuseIPDB', url: 'https://www.abuseipdb.com/register', required: false, free: false },
    { key: 'shodan', name: 'Shodan', url: 'https://account.shodan.io/register', required: false, free: false },
    { key: 'alienvault', name: 'AlienVault OTX', url: 'https://otx.alienvault.com/', required: false, free: true },
    { key: 'crowdsec', name: 'CrowdSec', url: 'https://app.crowdsec.net/', required: false, free: true },
    { key: 'hunterio', name: 'Hunter.io', url: 'https://hunter.io/users/sign_up', required: false, free: false },
    { key: 'emailrepio', name: 'EmailRep.io', url: 'https://emailrep.io/', required: false, free: true },
    { key: 'hibp', name: 'Have I Been Pwned', url: 'https://haveibeenpwned.com/API/Key', required: false, free: false },
    { key: 'pulsedive', name: 'Pulsedive', url: 'https://pulsedive.com/', required: false, free: true },
    { key: 'safebrowsing', name: 'Google Safe Browsing', url: 'https://developers.google.com/safe-browsing', required: false, free: true },
    { key: 'maltiverse', name: 'Maltiverse', url: 'https://maltiverse.com/', required: false, free: true },
    { key: 'ipqualityscore', name: 'IPQualityScore', url: 'https://www.ipqualityscore.com/', required: false, free: false },
    { key: 'checkphishai', name: 'CheckPhish.ai', url: 'https://checkphish.ai/', required: false, free: true },
    { key: 'nist_nvd', name: 'NIST NVD', url: 'https://nvd.nist.gov/developers/request-an-api-key', required: false, free: true },
  ];

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${OSINT_BACKEND}/api/osint/apikeys/`);
      const keysMap = {};
      response.data.forEach(item => {
        keysMap[item.name] = { key: item.key || '', enabled: item.is_active };
      });
      setApiKeys(keysMap);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      // Initialize with empty values
      const emptyKeys = {};
      osintApiKeys.forEach(api => {
        emptyKeys[api.key] = { key: '', enabled: false };
      });
      setApiKeys(emptyKeys);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyChange = (service, value) => {
    setApiKeys(prev => ({
      ...prev,
      [service]: { ...prev[service], key: value }
    }));
  };

  const handleToggle = async (service, enabled) => {
    // Update local state immediately
    setApiKeys(prev => ({
      ...prev,
      [service]: { ...prev[service], enabled }
    }));
    
    // Auto-save the toggle change
    try {
      const keyData = apiKeys[service] || { key: '', enabled: false };
      
      try {
        await axios.post(`${OSINT_BACKEND}/api/osint/apikeys/`, {
          name: service,
          key: keyData.key || '',
          is_active: enabled
        });
      } catch (err) {
        if (err.response?.status === 409) {
          await axios.delete(`${OSINT_BACKEND}/api/osint/apikeys?name=${service}`);
          await axios.post(`${OSINT_BACKEND}/api/osint/apikeys/`, {
            name: service,
            key: keyData.key || '',
            is_active: enabled
          });
        } else {
          throw err;
        }
      }
      
      toast.success(`${service} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle service:', error);
      toast.error(`Failed to update ${service}`);
      // Revert on error
      setApiKeys(prev => ({
        ...prev,
        [service]: { ...prev[service], enabled: !enabled }
      }));
    }
  };

  const handleSaveApiKey = async (service) => {
    setSaving(true);
    try {
      const keyData = apiKeys[service] || { key: '', enabled: false };
      
      // Try to create or update the API key
      try {
        await axios.post(`${OSINT_BACKEND}/api/osint/apikeys/`, {
          name: service,
          key: keyData.key,
          is_active: keyData.enabled
        });
      } catch (err) {
        // If key exists (409), try to delete and recreate
        if (err.response?.status === 409) {
          await axios.delete(`${OSINT_BACKEND}/api/osint/apikeys?name=${service}`);
          await axios.post(`${OSINT_BACKEND}/api/osint/apikeys/`, {
            name: service,
            key: keyData.key,
            is_active: keyData.enabled
          });
        } else {
          throw err;
        }
      }
      
      toast.success(`${service} API key saved successfully`);
    } catch (error) {
      console.error('Failed to save API key:', error);
      toast.error(`Failed to save ${service} API key: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllApiKeys = async () => {
    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    for (const api of osintApiKeys) {
      const keyData = apiKeys[api.key];
      if (keyData?.key) {
        try {
          try {
            await axios.post(`${OSINT_BACKEND}/api/osint/apikeys/`, {
              name: api.key,
              key: keyData.key,
              is_active: keyData.enabled !== false
            });
          } catch (err) {
            if (err.response?.status === 409) {
              await axios.delete(`${OSINT_BACKEND}/api/osint/apikeys?name=${api.key}`);
              await axios.post(`${OSINT_BACKEND}/api/osint/apikeys/`, {
                name: api.key,
                key: keyData.key,
                is_active: keyData.enabled !== false
              });
            } else {
              throw err;
            }
          }
          successCount++;
        } catch (error) {
          console.error(`Failed to save ${api.key}:`, error);
          errorCount++;
        }
      }
    }

    setSaving(false);
    if (successCount > 0) {
      toast.success(`Saved ${successCount} API key(s) successfully`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to save ${errorCount} API key(s)`);
    }
  };

  // Module Card Component
  const ModuleCard = ({ name, code, description, features, icon, enabled, color }) => {
    const colorClasses = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
      orange: 'bg-orange-50 border-orange-200 text-orange-700',
      green: 'bg-green-50 border-green-200 text-green-700'
    };

    const iconBgClasses = {
      blue: 'bg-blue-100',
      purple: 'bg-purple-100',
      orange: 'bg-orange-100',
      green: 'bg-green-100'
    };

    return (
      <div className={`p-4 rounded-lg border-2 ${enabled ? colorClasses[color] : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${enabled ? iconBgClasses[color] : 'bg-slate-200'}`}>
              {icon}
            </div>
            <div>
              <p className="font-semibold">{name}</p>
              <p className="text-xs opacity-80">{description}</p>
            </div>
          </div>
          {enabled ? (
            <Badge variant="default" className="bg-green-500 text-white">
              <Unlock className="w-3 h-3 mr-1" />
              Active
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-slate-200 text-slate-600">
              <Lock className="w-3 h-3 mr-1" />
              Locked
            </Badge>
          )}
        </div>
        <div className="space-y-1">
          {features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {enabled ? (
                <CheckCircle className="w-3 h-3 text-green-500" />
              ) : (
                <Lock className="w-3 h-3 text-slate-400" />
              )}
              <span className={enabled ? '' : 'text-slate-400'}>{feature}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account and system preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="plan" className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            Plan & Modules
          </TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          {canManageUsers && (
            <TabsTrigger value="rbac" className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              RBAC
            </TabsTrigger>
          )}
          <TabsTrigger value="integrations" className="flex items-center gap-1">
            <Plug className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="osint-api">OSINT API Keys</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                <p className="font-medium">{user.full_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                <p className="font-medium">{user.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Role</p>
                <p className="font-medium capitalize">{user.role?.replace('_', ' ')}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plan" className="mt-6 space-y-6">
          {/* Current Plan Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    Your Current Plan
                  </CardTitle>
                  <CardDescription>View your subscription details and enabled modules</CardDescription>
                </div>
                {isSuperAdmin && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                    <Crown className="w-3 h-3 mr-1" />
                    Super Admin
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Organization Info */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{currentTenant?.name || user.organization_name || 'Your Organization'}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="default" className="bg-indigo-600 mb-1">
                    {isSuperAdmin ? 'Enterprise' : currentTenant?.license_tier || 'Professional'}
                  </Badge>
                  <p className="text-xs text-muted-foreground">Plan</p>
                </div>
              </div>

              {/* Plan Features Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600">{isSuperAdmin ? '∞' : currentTenant?.user_limit || 100}</p>
                  <p className="text-xs text-muted-foreground">Max Users</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{isSuperAdmin ? '∞' : currentTenant?.cloud_accounts_limit || 10}</p>
                  <p className="text-xs text-muted-foreground">Cloud Accounts</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{isSuperAdmin ? '∞' : currentTenant?.scans_per_month || 50}</p>
                  <p className="text-xs text-muted-foreground">Scans/Month</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{isSuperAdmin ? 'All' : (currentTenant?.enabled_modules?.length || 2)}</p>
                  <p className="text-xs text-muted-foreground">Modules</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Enabled Modules Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Enabled Modules
              </CardTitle>
              <CardDescription>
                These are the modules available in your current plan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* OSINT Module */}
                <ModuleCard
                  name="OSINT Toolkit"
                  code="osint"
                  description="Threat intelligence & IOC analysis"
                  features={['IOC Analyzer', 'Domain Lookup', 'IP Reputation', 'Hash Analysis', 'Breach Monitoring']}
                  icon="🔍"
                  enabled={isSuperAdmin || currentTenant?.enabled_modules?.includes('osint')}
                  color="blue"
                />

                {/* GRC Module */}
                <ModuleCard
                  name="GRC Nexus"
                  code="grc"
                  description="Governance, Risk & Compliance management"
                  features={['Framework Management', 'Control Tracking', 'Risk Assessment', 'Compliance Reporting', 'Policy Management']}
                  icon="📋"
                  enabled={isSuperAdmin || currentTenant?.enabled_modules?.includes('grc')}
                  color="purple"
                />

                {/* LiteSabre Module */}
                <ModuleCard
                  name="LiteSabre Scanner"
                  code="litesabre"
                  description="Vulnerability scanning & SBOM analysis"
                  features={['Code Scanning', 'SBOM Generation', 'Dependency Analysis', 'CVE Detection', 'License Compliance']}
                  icon="⚔️"
                  enabled={isSuperAdmin || currentTenant?.enabled_modules?.includes('litesabre') || currentTenant?.enabled_modules?.includes('grc')}
                  color="orange"
                />

                {/* Amidala CSPM Module */}
                <ModuleCard
                  name="Amidala CSPM"
                  code="amidala"
                  description="Cloud Security Posture Management"
                  features={['AWS Scanning', 'Azure Scanning', 'GCP Scanning', 'Misconfiguration Detection', 'CIS Benchmarks']}
                  icon="☁️"
                  enabled={isSuperAdmin || currentTenant?.enabled_modules?.includes('amidala') || currentTenant?.enabled_modules?.includes('grc')}
                  color="green"
                />

                {/* DPDPA Consent Manager Module */}
                <ModuleCard
                  name="DPDPA Consent Manager"
                  code="dpdpa"
                  description="India's Digital Personal Data Protection"
                  features={['Consent Management', 'Data Subject Requests', 'PII Scanner', 'Jira Integration', 'Automated Alerts']}
                  icon="🛡️"
                  enabled={isSuperAdmin || currentTenant?.enabled_modules?.includes('dpdpa')}
                  color="purple"
                />
              </div>

              {/* Upgrade CTA */}
              {!isSuperAdmin && (
                <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-indigo-900">Need more modules?</p>
                      <p className="text-sm text-indigo-700">Upgrade your plan to unlock additional features</p>
                    </div>
                    <Button variant="default" className="bg-indigo-600 hover:bg-indigo-700">
                      Upgrade Plan
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm">Two-Factor Authentication</span>
                <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">Not Enabled</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm">Session Timeout</span>
                <span className="text-xs text-muted-foreground">20 minutes (inactivity)</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <NotificationPreferences />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <UserManagement />
        </TabsContent>

        <TabsContent value="rbac" className="mt-6">
          <RBACSettings />
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          <IntegrationsSettings />
        </TabsContent>

        <TabsContent value="osint-api" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                OSINT Toolkit API Keys
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Configure API keys for threat intelligence services. Enter your keys and click Save.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-green-900 mb-2">💡 Many sources are FREE!</h4>
                  <p className="text-sm text-green-800">
                    IOC Analyzer now includes 15+ free threat intelligence sources (GreyNoise, Shodan InternetDB, 
                    Spamhaus, CIRCL, etc.) that work without API keys. Optional paid services are listed below 
                    for enhanced coverage.
                  </p>
                </div>

                <div className="flex justify-end mb-4">
                  <Button onClick={handleSaveAllApiKeys} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save All API Keys
                      </>
                    )}
                  </Button>
                </div>

                {loading ? (
                  <div className="flex justify-center p-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Free Services - No API Key Needed */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Free Threat Intelligence (No API Key)
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        These services work without API keys. Toggle to enable/disable.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {osintApiKeys.filter(api => api.noKey).map((api) => (
                          <div key={api.key} className="flex items-center justify-between p-3 border rounded-lg bg-green-50/50">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm">{api.name}</h4>
                              <a
                                href={api.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                            <Switch
                              checked={apiKeys[api.key]?.enabled !== false}
                              onCheckedChange={(checked) => handleToggle(api.key, checked)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Services Requiring API Keys */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        API Key Required
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        These services require API keys for enhanced features.
                      </p>
                      <div className="space-y-3">
                        {osintApiKeys.filter(api => !api.noKey).map((api) => (
                          <div key={api.key} className="flex items-center gap-4 p-4 border rounded-lg bg-white">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{api.name}</h4>
                                {api.free && (
                                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Free tier</span>
                                )}
                                {api.required && (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Recommended</span>
                                )}
                              </div>
                              <Input
                                type="password"
                                placeholder={`Enter ${api.name} API key`}
                                value={apiKeys[api.key]?.key || ''}
                                onChange={(e) => handleKeyChange(api.key, e.target.value)}
                                className="mt-1"
                              />
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={apiKeys[api.key]?.enabled || false}
                                  onCheckedChange={(checked) => handleToggle(api.key, checked)}
                                />
                                <span className="text-xs text-muted-foreground">Active</span>
                              </div>
                              <a
                                href={api.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleSaveApiKey(api.key)}
                                disabled={saving}
                              >
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="jira" className="mt-6">
          {/* Legacy Jira tab — the Jira Cloud connection now lives under
              Integrations so both DPDPA (DSR tickets) and Amidala CSPM
              (finding tickets) share one tenant-scoped configuration. This
              tab still hosts the Admin DB config for backward compatibility
              with bookmarked URLs; the Jira form was removed to avoid the
              "configured in two places, only one is read" bug. */}
          <Card data-testid="jira-tab-moved-notice" className="border-amber-200 bg-amber-50/50 mb-4">
            <CardContent className="pt-5 pb-5 flex items-start gap-3">
              <ExternalLink className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">
                  Jira configuration moved to Integrations
                </div>
                <div className="text-amber-800 mt-1">
                  Go to{' '}
                  <a
                    className="underline font-medium"
                    href="?tab=integrations"
                    data-testid="jira-tab-move-link"
                  >
                    Settings → Integrations → Jira Cloud
                  </a>{' '}
                  to save your Jira credentials. Both DSR ticketing and Amidala finding
                  export read from the same tenant-scoped connection.
                </div>
              </div>
            </CardContent>
          </Card>
          <AdminDBConfigSection />
        </TabsContent>
      </Tabs>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5" />
            System
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-sm">Version</span>
            <span className="text-xs text-muted-foreground">3.0.0</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm">Last Updated</span>
            <span className="text-xs text-muted-foreground">06/04/2026</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;